import React, { useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PivotTable from './PivotTable';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import { useTranslation } from 'react-i18next';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import Table from '../../../components/material-ui/adaptive/Table';
import { ENUM_DEPOSIT_STATUS } from '../../../enums/enums';
import TypographyTitle from '../../../components/material-ui/table/TypographyTitle';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import TypographyLink from '../../../components/material-ui/table/TypographyLink';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import CusLink from '../../../components/material-ui/CusLink';
import DepositGatewayReplies from './modal/DepositGatewayReplies';

// depositInfo.list
// 0   - числовой идентификатор заявки на пополнение
// 1   - символьный идентификатор транзакции платежной системы
// 2   - символьный идентификатор сделки платежной системы
// 3   - числовой идентификатор пользователя
// 4   - сумма заявки в валюте
// 5   - символьный код валюты
// 6   - сумма пополнения баланса за вычетом комиссии казино
// 7   - сумма комиссии шлюза в валюте платежа (виртуальная, для информации)
// 8   - обозначение платежного шлюза (см. "/input/gate_list.php")
// 9   - статус пополнения (см. "/input/status_list.php")
// 10 - дата создания записи в формате 'YYYY-MM-DD hh:mm:ss'
// 11 - дата обновления записи в формате 'YYYY-MM-DD hh:mm:ss'
// 12 - реальная сумма пополнения пользователем

export default function DepositPlayersTable({
  alreadyShow,
  btnLoading,
  depositInfo,
  gates,
  statusHash,
  startPageNumber = 1
}) {
  const { t } = useTranslation();
  const [openRawId, setOpenRawId] = useState(null);

  const {
    options = {},
    groupList: groupMap = [],
    list: deposits = [],
    userList: userMap = [],
    casinoList: casinoMap = []
  } = depositInfo;

  const getViewDataTime = (dateTime) => {
    return getOnlyData(dayjs(dateTime), '', 'DD.MM.YYYY HH:mm');
  };

  const gatesNameByCode = gates.reduce((acc, item) => {
    acc[item.code] = item.label;
    return acc;
  }, {});

  const gateNameById = (gateId) => gatesNameByCode[gateId] || gateId;

  return (
    <>
      <Stack gap={4}>
        <ShowContainer
          condition={deposits.length}
          plug={
            <ShowContainer condition={alreadyShow && !btnLoading}>
              <Box
                sx={{
                  background: (theme) => theme.palette.grey[200],
                  color: (th) => th.palette.text.secondary,
                  borderRadius: 1,
                  p: 3
                }}>
                <Typography fontSize={16} fontWeight={600}>
                  {t('No records found. Please update your filters.')}
                </Typography>
              </Box>
            </ShowContainer>
          }>
          <PivotTable deposits={deposits} />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <MyTableBoldCell isLess sx={{ p: 1, pr: 0 }}>
                    #
                  </MyTableBoldCell>
                  <MyTableCell isLess>
                    <Stack gap={0.5}>
                      <TypographyTitle>{t('Record ID')}</TypographyTitle>
                      <TypographyTitle>{t('Transaction ID')}</TypographyTitle>
                      <TypographyTitle>{t('Deal ID')}</TypographyTitle>
                    </Stack>
                  </MyTableCell>
                  <MyTableBoldCell isLess>{t('User')}</MyTableBoldCell>
                  <MyTableCell isLess>
                    <Stack>
                      <TypographyTitle>{t('Application amount')}/</TypographyTitle>
                      <TypographyTitle>{t('replenishment')}</TypographyTitle>
                    </Stack>
                  </MyTableCell>
                  <MyTableCell isLess>
                    <Stack>
                      <TypographyTitle>{t('Received casino')}/</TypographyTitle>
                      <TypographyTitle>{t('Gateway commission')}</TypographyTitle>
                    </Stack>
                  </MyTableCell>
                  <MyTableBoldCell isLess>{t('Invoice status')}</MyTableBoldCell>
                  <MyTableCell isLess>
                    <Stack>
                      <TypographyTitle>{t('Date Created')}/</TypographyTitle>
                      <TypographyTitle>{t('Modified')}</TypographyTitle>
                    </Stack>
                  </MyTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((info, indx) => {
                  const userId = info[3];
                  const transId = info[1];
                  const userInfo = userMap[userId] || '';
                  const groupId = userInfo[1];
                  const group = groupMap[groupId] || '';
                  const casinoId = group[1];
                  const casinoName = casinoMap[casinoId] || '';
                  const statusName = ENUM_DEPOSIT_STATUS[statusHash[info[9]]] || '';
                  const currency = info[5];
                  const groupName = group[0];

                  return (
                    <TableRow key={info[0]}>
                      <MyTableCell sx={{ p: 1, pr: 0 }}>{startPageNumber + indx}</MyTableCell>
                      <MyTableCell isLess>
                        <Stack>
                          <TypographyText>
                            #{info[0]} ({gateNameById(info[8])})
                          </TypographyText>
                          <TypographyText isCopy={transId}>{transId}</TypographyText>
                          <TypographyText>{info[2]}</TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack gap={0.5}>
                          <TypographyText>
                            {userId}:{' '}
                            <TypographyText
                              isCopy={userInfo[0]}
                              sx={{ wordBreak: 'break-all', p: 0 }}>
                              {userInfo[0]}
                            </TypographyText>
                          </TypographyText>

                          <CusLink text="User log" to={`/users/logs/${userId}`} />
                          <CusLink text="IP address" to={`/users/IpAddresses/${userId}`} />

                          <CusLink
                            text={'Financial profile'}
                            to={`/users/finance/${userId}/${currency}`}
                          />

                          <TypographyText>
                            {t('Group')}: <CusLink text={groupName} to={`/groups/${groupId}`} />
                          </TypographyText>

                          <TypographyText>
                            {t('Casino')}:{' '}
                            <CusLink text={casinoName} to={`/settings/casino/${casinoId}`} />
                          </TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack alignItems={'end'}>
                          <TypographyText>
                            {info[4]} {currency}
                          </TypographyText>
                          <TypographyText>
                            {info[6]} {currency}
                          </TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack alignItems={'end'}>
                          <TypographyText>
                            {info[12]} {currency}
                          </TypographyText>
                          <TypographyText>
                            {info[7]} {currency}
                          </TypographyText>
                        </Stack>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Typography>{t(statusName)}</Typography>
                        <ShowContainer condition={options.viewRaw}>
                          <TypographyLink onClick={() => setOpenRawId(info[0])}>
                            {t('Gateway replies')}
                          </TypographyLink>
                        </ShowContainer>
                      </MyTableCell>
                      <MyTableCell isLess>
                        <Stack>
                          <TypographyText>{getViewDataTime(info[10])}</TypographyText>
                          <TypographyText>{getViewDataTime(info[11])}</TypographyText>
                        </Stack>
                      </MyTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </ShowContainer>
      </Stack>

      <DepositGatewayReplies
        openRawId={openRawId}
        setOpenRawId={setOpenRawId}
        depositInfo={deposits?.find((el) => el[0] == openRawId) || {}}
      />
    </>
  );
}
